<template>
  <app-content class="h-panel">
    <Loading text="数据加载中..." :loading="loading"></Loading>
    <div class="h-panel-bar">
      <span class="h-panel-title">快照详情</span>
      <Button class="float-right" @click="$router.push({ name: 'ReportSnapshotList' })">返回列表</Button>
      <Button
        v-if="canManage"
        class="float-right margin-right"
        color="primary"
        @click="openShareModal"
      >分享设置</Button>
    </div>

    <div class="margin-left margin-right margin-top" v-if="snapshot">
      <div class="meta-line">
        <span>标题：{{ snapshot.title || '-' }}</span>
      </div>
      <div class="meta-line">
        <span>备注：{{ snapshot.remark || '-' }}</span>
      </div>
      <div class="meta-line">
        <span>快照编号：{{ snapshot.snapshotNo }}</span>
        <span>会计期间：{{ formatDate(snapshot.accountDate) }}</span>
        <span>创建人：{{ snapshot.createdByName || '-' }}</span>
        <span>创建时间：{{ formatDateTime(snapshot.createTime) }}</span>
      </div>
      <div class="meta-line" v-if="share && share.hasShare">
        <span>分享模式：{{ share.accessMode === 'PASSWORD' ? '密码访问' : '公开访问' }}</span>
        <span>过期时间：{{ formatDateTime(share.expiredAt) }}</span>
        <span>访问次数：{{ share.viewCount || 0 }}</span>
      </div>
    </div>

    <div class="h-panel-body" v-if="reports.length > 0">
      <Tabs :datas="tabDatas" v-model="activeReportKey" class="h-tabs-card" />

      <Table :datas="currentItems" border class="small-td margin-top">
        <TableItem title="项目">
          <div slot-scope="{data}" :class="{'font-bold': data.isBolder}" :style="{paddingLeft:((data.level-1)*15)+'px'}">{{data.title}}</div>
        </TableItem>
        <TableItem title="行次" :width="70">
          <template slot-scope="{data}">
            {{data.isClassified ? '' : (data.lineNum > -1 ? data.lineNum : '')}}
          </template>
        </TableItem>

        <TableItem title="最终本年" align="right">
          <template slot-scope="{data}">{{ data.currentYearAmount | numFormat }}</template>
        </TableItem>
        <TableItem title="最终本期" align="right">
          <template slot-scope="{data}">{{ data.currentPeriodAmount | numFormat }}</template>
        </TableItem>
        <TableItem v-if="showLastYear" title="最终上年" align="right">
          <template slot-scope="{data}">{{ data.lastYearAmount | numFormat }}</template>
        </TableItem>

        <TableItem title="调整本年" align="right">
          <template slot-scope="{data}">{{ data.adjustCurrentYearAmount | numFormat }}</template>
        </TableItem>
        <TableItem title="调整本期" align="right">
          <template slot-scope="{data}">{{ data.adjustCurrentPeriodAmount | numFormat }}</template>
        </TableItem>
        <TableItem v-if="showLastYear" title="调整上年" align="right">
          <template slot-scope="{data}">{{ data.adjustLastYearAmount | numFormat }}</template>
        </TableItem>
      </Table>
    </div>

    <Modal v-model="shareModalVisible" title="快照分享设置" width="520" v-if="canManage">
      <Form :model="shareForm" :labelWidth="90">
        <FormItem label="访问模式">
          <Select v-model="shareForm.accessMode" :datas="accessModeOptions" keyName="key" titleName="title" />
        </FormItem>
        <FormItem v-if="shareForm.accessMode === 'PASSWORD'" label="访问密码">
          <input class="h-input" type="password" v-model="shareForm.password" placeholder="请输入访问密码" />
        </FormItem>
        <FormItem label="过期日期">
          <DatePicker type="date" v-model="shareForm.expiredAt" />
        </FormItem>
      </Form>

      <div v-if="share && share.hasShare" class="share-result">
        <div>当前链接：{{ shareLink }}</div>
        <Button size="s" color="primary" @click="copyShareLink">复制链接</Button>
      </div>

      <div slot="footer">
        <Button @click="shareModalVisible = false">取消</Button>
        <Button color="primary" :loading="shareLoading" @click="saveShare">保存</Button>
      </div>
    </Modal>
  </app-content>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'ReportSnapshotDetail',
  props: {
    snapshotId: [Number, String]
  },
  data() {
    return {
      loading: false,
      snapshot: null,
      reports: [],
      share: null,
      activeReportKey: '',
      shareModalVisible: false,
      shareLoading: false,
      shareForm: {
        accessMode: 'PUBLIC',
        password: '',
        expiredAt: null
      },
      accessModeOptions: [
        { key: 'PUBLIC', title: '公开访问' },
        { key: 'PASSWORD', title: '密码访问' }
      ]
    }
  },
  computed: {
    ...mapState(['User']),
    canManage() {
      return this.User && this.User.role !== 'View';
    },
    tabDatas() {
      return this.reports.map(r => ({ key: r.reportKey, title: r.reportName }));
    },
    currentReport() {
      return this.reports.find(r => r.reportKey === this.activeReportKey) || null;
    },
    currentItems() {
      return this.currentReport ? this.currentReport.items : [];
    },
    showLastYear() {
      return this.currentReport && this.currentReport.reportType === 3;
    },
    shareLink() {
      if (!this.share || !this.share.hasShare) {
        return '';
      }
      return `${window.location.origin}${this.share.urlPath}`;
    }
  },
  methods: {
    loadDetail() {
      this.loading = true;
      this.$api.report.snapshot.detail(this.snapshotId).then(({ data }) => {
        this.snapshot = data.snapshot;
        this.reports = data.reports || [];
        this.share = data.share || { hasShare: false };
        if (this.reports.length > 0) {
          this.activeReportKey = this.reports[0].reportKey;
        }
      }).finally(() => {
        this.loading = false;
      });
    },
    openShareModal() {
      this.shareForm.accessMode = this.share && this.share.accessMode ? this.share.accessMode : 'PUBLIC';
      this.shareForm.password = '';
      this.shareForm.expiredAt = this.share && this.share.expiredAt ? new Date(this.share.expiredAt) : null;
      this.shareModalVisible = true;
    },
    saveShare() {
      if (this.shareForm.accessMode === 'PASSWORD' && !this.shareForm.password) {
        this.$Message.error('密码访问模式必须输入密码');
        return;
      }
      this.shareLoading = true;
      this.$api.report.snapshot.saveShare(this.snapshotId, {
        accessMode: this.shareForm.accessMode,
        password: this.shareForm.password,
        expiredAt: this.shareForm.expiredAt
      }).then(({ data }) => {
        this.share = data;
        this.$Message.success('分享设置已保存');
        this.shareModalVisible = false;
      }).finally(() => {
        this.shareLoading = false;
      });
    },
    copyShareLink() {
      if (!this.shareLink) {
        return;
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.shareLink).then(() => {
          this.$Message.success('链接已复制');
        }).catch(() => {
          this.fallbackCopy();
        });
      } else {
        this.fallbackCopy();
      }
    },
    fallbackCopy() {
      const input = document.createElement('input');
      input.value = this.shareLink;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      this.$Message.success('链接已复制');
    },
    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      if (isNaN(d.getTime())) return date;
      return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}`;
    },
    formatDateTime(date) {
      if (!date) return '-';
      const d = new Date(date);
      if (isNaN(d.getTime())) return date;
      return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())} ${this.pad(d.getHours())}:${this.pad(d.getMinutes())}`;
    },
    pad(n) {
      return n < 10 ? `0${n}` : `${n}`;
    }
  },
  mounted() {
    this.loadDetail();
  }
}
</script>

<style scoped>
.margin-right {
  margin-right: 10px;
}
.meta-line {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 8px;
}
.font-bold {
  font-size: 14px;
}
.share-result {
  margin-top: 10px;
  padding: 8px;
  background: #f7f7f7;
}
</style>
